import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SITE_CONFIG } from './config';
import { useTheme } from './App';

function DealsPage() {
  const { theme } = useTheme();
  const API_URL = SITE_CONFIG.apiUrl;
  const { retailer } = useParams();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState(retailer || 'all');
  const [sortBy, setSortBy] = useState('recent');
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [retailers, setRetailers] = useState(['all']);
  const [hasMore, setHasMore] = useState(true);
  const dealsPerPage = 24;

  const dealsGridRef = useRef(null);

  // Helper function to replace affiliate tag in URLs
  const updateAffiliateTag = (url) => {
    if (!url) return url;

    // List of old affiliate tags to replace
    const oldTags = [
      'dealsniper0d-20',
      'odealia0d-20',
      // Add other old tags as needed
    ];

    let updatedUrl = url;

    // Replace old affiliate tags with new one
    oldTags.forEach(oldTag => {
      const tagPattern = new RegExp(`tag=${oldTag}`, 'gi');
      updatedUrl = updatedUrl.replace(tagPattern, `tag=${SITE_CONFIG.affiliateTag}`);
    });

    // If no tag found, add our tag
    if (!updatedUrl.includes('tag=')) {
      const separator = updatedUrl.includes('?') ? '&' : '?';
      updatedUrl += `${separator}tag=${SITE_CONFIG.affiliateTag}`;
    }

    return updatedUrl;
  };

  // Helper function to process deals and update affiliate tags
  const processDeals = useCallback((rawDeals) => {
    return rawDeals.map(deal => ({
      ...deal,
      url: updateAffiliateTag(deal.url)
    }));
  }, []);

  // Fetch retailers dynamically from the backend
  useEffect(() => {
    fetch(`${API_URL}/v2/retailers`)
      .then(response => response.json())
      .then(data => setRetailers(['all', ...data]))
      .catch(error => {
        console.error('Error fetching retailers:', error);
        setError('Failed to load retailers. Using defaults.');
        setRetailers(['all', 'amazon', 'target', 'bestbuy']);
      });
  }, [API_URL]);

  // Fetch deals based on retailer, sortBy, and offset
  useEffect(() => {
    let url = `${API_URL}/v2/deals?sort=${sortBy}&limit=${dealsPerPage}&offset=${offset}`;
    if (selectedRetailer !== 'all') {
      url += `&retailer=${selectedRetailer}`;
    }
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(data => {
        const processedDeals = processDeals(data);
        setDeals(processedDeals);
        setHasMore(data.length === dealsPerPage);
        setError(null);
      })
      .catch(error => {
        console.error('Error fetching deals:', error);
        setError('Failed to load deals. Please try again.');
      });
  }, [selectedRetailer, sortBy, offset, API_URL, processDeals]);

  // Real-time updates for new deals (3-minute interval)
  useEffect(() => {
    let url = `${API_URL}/v2/new_deals?since=${new Date().toISOString()}`;
    if (selectedRetailer !== 'all') {
      url += `&retailer=${selectedRetailer}`;
    }
    const timer = setInterval(() => {
      fetch(url)
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.json();
        })
        .then(newDeals => {
          if (newDeals.length > 0 && offset === 0) {
            const processedNewDeals = processDeals(newDeals);
            setDeals(prev => [...processedNewDeals, ...prev].slice(0, dealsPerPage));
            setError(null);
          }
        })
        .catch(error => {
          console.error('Error fetching new deals:', error);
          setError('Failed to load new deals. Updates may be delayed.');
        });
    }, 180000);
    return () => clearInterval(timer);
  }, [selectedRetailer, sortBy, offset, API_URL, processDeals]);

  // Handle retailer filter change
  const handleRetailerFilter = (e) => {
    const newRetailer = e.target.value;
    setSelectedRetailer(newRetailer);
    setOffset(0);
    if (newRetailer === 'all') {
      navigate('/deals');
    } else {
      navigate(`/deals/${newRetailer}`);
    }
  };

  // Handle sort change
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    setOffset(0);
  };

  // Handle Next button
  const handleNext = () => {
    setOffset(prev => prev + dealsPerPage);
    if (dealsGridRef.current) {
      dealsGridRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle Previous button
  const handlePrevious = () => {
    setOffset(prev => Math.max(prev - dealsPerPage, 0));
    if (dealsGridRef.current) {
      dealsGridRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.primary.bg} ${theme.primary.text}`}>
      {/* Header */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="p-4 md:p-6 lg:p-12 mb-4 md:mb-8 text-center"
      >
        <h1 className={`text-2xl md:text-4xl lg:text-5xl font-extrabold bg-gradient-to-r ${theme.primary.accent} bg-clip-text text-transparent drop-shadow-2xl mb-3 md:mb-4`}>
          {selectedRetailer === 'all' ? 'All Deals' : `${selectedRetailer.charAt(0).toUpperCase() + selectedRetailer.slice(1)} Deals`}
        </h1>
        <p className={`text-sm md:text-lg lg:text-xl ${theme.primary.textSecondary} ${theme.primary.card} p-2 md:p-3 rounded-xl border inline-block`}>
          Discover incredible savings with premium deals!
        </p>
      </motion.header>

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 md:p-6 lg:p-12 text-red-400 text-center backdrop-blur-sm bg-red-500/10 mx-4 md:mx-6 rounded-xl border border-red-400/30"
        >
          {error}
        </motion.div>
      )}

      {/* Sorting and Retailer Filter Dropdowns */}
      <div className="p-4 md:p-6 lg:p-12 flex justify-center md:justify-end mb-4 md:mb-6 space-x-2 md:space-x-4">
        {/* Retailer Filter */}
        <motion.select
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3 }}
          value={selectedRetailer}
          onChange={handleRetailerFilter}
          className={`p-2 md:p-3 ${theme.primary.card} border rounded-xl shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 ${theme.primary.text} placeholder-gray-300 text-sm md:text-base`}
        >
          {retailers.map((ret) => (
            <option key={ret} value={ret} className="bg-gray-800 text-white">
              {ret.charAt(0).toUpperCase() + ret.slice(1)}
            </option>
          ))}
        </motion.select>

        {/* Sorting Dropdown */}
        <motion.select
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3 }}
          value={sortBy}
          onChange={handleSortChange}
          className={`p-2 md:p-3 ${theme.primary.card} border rounded-xl shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 ${theme.primary.text} placeholder-gray-300 text-sm md:text-base`}
        >
          <option value="recent" className="bg-gray-800 text-white">Most Recent</option>
          <option value="discount" className="bg-gray-800 text-white">Highest Discount %</option>
        </motion.select>
      </div>

      {/* Deals Grid */}
      <motion.div
        ref={dealsGridRef}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="p-4 md:p-6 lg:p-12 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6 lg:gap-8"
      >
        {deals.length > 0 ? (
          deals.map((deal, index) => (
            <motion.div
              key={deal.id || deal.url || deal.timestamp || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              className={`${theme.primary.card} rounded-2xl border shadow-2xl p-3 md:p-4 lg:p-6 hover:${theme.primary.cardHover} transition-all duration-300`}
            >
              <div className={`bg-gradient-to-r ${theme.primary.discountBadge} text-white text-xs md:text-sm font-bold px-2 py-1 md:px-3 md:py-2 rounded-full shadow-lg mb-2 md:mb-4 w-fit`}>
                {deal.discount}% Off
              </div>
              {deal.image_url && (
                <img
                  src={deal.image_url}
                  alt={deal.title}
                  className={`w-full h-24 md:h-36 lg:h-48 object-contain rounded-xl mb-2 md:mb-4 ${theme.primary.card} p-1 md:p-2`}
                />
              )}
              <p className={`text-xs md:text-sm lg:text-lg font-medium ${theme.primary.text} truncate mb-2 md:mb-4 leading-relaxed`}>
                {deal.title}
              </p>
              <button
                onClick={() => window.open(deal.url, '_blank')}
                className={`bg-gradient-to-r ${theme.primary.button} text-white px-3 py-1.5 md:px-4 md:py-2 lg:px-6 lg:py-3 rounded-full flex items-center justify-center hover:${theme.primary.buttonHover} transition-all duration-300 w-full mb-2 md:mb-4 shadow-xl font-semibold text-xs md:text-sm lg:text-base`}
              >
                Get Deal <span className="ml-1 md:ml-2 text-sm md:text-lg lg:text-xl">→</span>
              </button>
              <p className="text-sm md:text-lg lg:text-2xl font-bold text-green-400 mt-1 md:mt-2">Now: ${deal.current_price}</p>
              <p className={`text-xs md:text-sm ${theme.primary.textMuted} mt-0.5 md:mt-1 line-through`}>Was: ${deal.regular_price}</p>
              {deal.promo && (
                <p className="text-xs md:text-sm text-green-300 mt-1 md:mt-2">Promo: {deal.promo.replace('Promo: ', '')}</p>
              )}
              {deal.coupon && (
                <p className="text-xs md:text-sm text-green-300 mt-0.5 md:mt-1">Coupon: {deal.coupon.replace('Coupon: ', '')}</p>
              )}
              {deal.availability && (
                <p className={`text-xs md:text-sm ${theme.primary.textMuted} mt-0.5 md:mt-1`}>Availability: {deal.availability}</p>
              )}
              <p className={`text-xs md:text-sm ${theme.primary.textMuted} mt-1 md:mt-2`}>Added: {new Date(deal.timestamp).toLocaleDateString()}</p>
            </motion.div>
          ))
        ) : (
          <p className={`${theme.primary.textMuted} text-center col-span-full text-sm md:text-lg lg:text-xl ${theme.primary.card} p-4 md:p-8 rounded-xl border`}>
            No deals available for this selection.
          </p>
        )}
      </motion.div>

      {/* Pagination */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="p-4 md:p-6 lg:p-12 mt-4 md:mt-8 flex justify-center items-center gap-3 md:gap-6 flex-wrap"
      >
        <motion.button
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handlePrevious}
          disabled={offset === 0}
          className={`bg-gradient-to-r ${theme.primary.button} text-white px-4 py-2 md:px-6 md:py-3 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl font-semibold text-sm md:text-base`}
        >
          Previous
        </motion.button>
        <motion.button
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.3 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleNext}
          disabled={!hasMore}
          className={`bg-gradient-to-r ${theme.primary.button} text-white px-4 py-2 md:px-6 md:py-3 rounded-full hover:${theme.primary.buttonHover} transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl font-semibold text-sm md:text-base`}
        >
          Next
        </motion.button>
      </motion.div>
    </div>
  );
}

export default DealsPage;
